import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { api, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";
import { CreditCard, Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";

// Loads PayPal JS SDK once
function loadPayPalSdk(clientId) {
  return new Promise((resolve, reject) => {
    if (window.paypal) return resolve(window.paypal);
    const existing = document.getElementById("paypal-sdk");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.paypal));
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.id = "paypal-sdk";
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=USD&intent=capture`;
    s.async = true;
    s.onload = () => resolve(window.paypal);
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

export default function BuyCreditsButton({ onSuccess }) {
  const { refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bundles, setBundles] = useState(1);
  const containerRef = useRef(null);
  const buttonsRef = useRef(null);

  const price = cfg ? (parseFloat(cfg.bundle_price) * bundles).toFixed(2) : "0.99";
  const credits = cfg ? cfg.bundle_credits * bundles : 2;

  useEffect(() => {
    api.get("/payments/config").then(r => setCfg(r.data)).catch(() => setCfg({ configured: false }));
  }, []);

  useEffect(() => {
    if (!open || !cfg?.configured || !cfg.client_id) return;
    let cancelled = false;
    setLoading(true);
    loadPayPalSdk(cfg.client_id)
      .then((paypal) => {
        if (cancelled || !containerRef.current) return;
        if (buttonsRef.current) {
          try { buttonsRef.current.close(); } catch {}
        }
        containerRef.current.innerHTML = "";
        const buttons = paypal.Buttons({
          style: { layout: "vertical", color: "gold", shape: "rect", label: "paypal" },
          createOrder: async () => {
            const r = await api.post("/payments/create-order", { bundles });
            return r.data.order_id;
          },
          onApprove: async (data) => {
            try {
              const r = await api.post("/payments/capture-order", { order_id: data.orderID });
              toast.success(`+${r.data.credits_added} credits added`);
              await refresh();
              onSuccess?.();
              setOpen(false);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Payment capture failed");
            }
          },
          onError: (err) => {
            console.error(err);
            toast.error("PayPal error — please try again");
          },
        });
        buttons.render(containerRef.current);
        buttonsRef.current = buttons;
      })
      .catch(() => toast.error("Failed to load PayPal"))
      .finally(() => setLoading(false));
    return () => { cancelled = true; };
  }, [open, cfg, bundles, refresh, onSuccess]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="buy-credits-paypal-button" className="bg-[#0F172A] hover:bg-[#1E293B]">
          <CreditCard size={16} className="mr-2"/> Buy credits
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="paypal-button-container">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">Top up credits</DialogTitle>
          <DialogDescription>
            ${cfg?.bundle_price || "0.99"} for every {cfg?.bundle_credits || 2} credits · PayPal Sandbox
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between p-4 rounded-lg bg-[#F4F0EA] border border-[#E2DBCE]">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#64748B] font-mono-jb">Bundle</div>
            <div className="font-serif-display text-xl font-semibold" data-testid="credits-bundle-display">
              {credits} credits
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-[#64748B] font-mono-jb">Total</div>
            <div className="font-serif-display text-2xl font-bold text-[#0F172A]" data-testid="credits-price-display">
              ${price}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 text-sm">
          <span className="text-[#475569]">Quantity:</span>
          {[1, 2, 4].map(n => (
            <button key={n}
              onClick={() => setBundles(n)}
              data-testid={`bundle-qty-${n}`}
              className={`px-3 py-1 rounded-md border text-sm ${bundles===n ? "bg-[#0F172A] text-white border-[#0F172A]" : "border-[#E2DBCE] hover:bg-[#F4F0EA]"}`}>
              ×{n}
            </button>
          ))}
        </div>

        {!cfg?.configured ? (
          <div className="p-4 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-900" data-testid="paypal-not-configured">
            PayPal sandbox credentials are not configured on the server yet. Add <span className="font-mono-jb">PAYPAL_CLIENT_ID</span> and <span className="font-mono-jb">PAYPAL_CLIENT_SECRET</span> to <span className="font-mono-jb">backend/.env</span>, then restart backend.
          </div>
        ) : (
          <>
            {loading && (
              <div className="flex items-center gap-2 text-sm text-[#475569]">
                <Loader2 size={14} className="animate-spin"/> Loading PayPal…
              </div>
            )}
            <div ref={containerRef} data-testid="paypal-buttons-slot" />
          </>
        )}

        <div className="flex items-center gap-2 text-xs text-[#64748B]">
          <ShieldCheck size={14} className="text-[#059669]"/>
          Payment is verified server-side before credits are applied.
        </div>
      </DialogContent>
    </Dialog>
  );
}
